import { onUnmounted, ref } from 'vue';

// Drag-the-right-edge resize for the studio's sidebar rails. The width is
// clamped to [min, max] and persisted to localStorage under `widthKey`, so each
// rail remembers its own size. Shared by EditorSideList and SegmentedSideList.
export interface RailResizeOptions {
  min?: number;
  max?: number;
  default?: number;
}

export function useRailResize(widthKey: string, opts: RailResizeOptions = {}) {
  const min = opts.min ?? 180;
  const max = opts.max ?? 520;
  const fallback = opts.default ?? 240;

  const stored = Number(localStorage.getItem(widthKey));
  const width = ref(stored >= min && stored <= max ? stored : fallback);
  const resizing = ref(false);
  let startX = 0;
  let startWidth = 0;

  function onResize(e: MouseEvent) {
    width.value = Math.max(min, Math.min(max, startWidth + (e.clientX - startX)));
  }

  function endResize() {
    resizing.value = false;
    localStorage.setItem(widthKey, String(width.value));
    window.removeEventListener('mousemove', onResize);
    window.removeEventListener('mouseup', endResize);
  }

  function startResize(e: MouseEvent) {
    resizing.value = true;
    startX = e.clientX;
    startWidth = width.value;
    window.addEventListener('mousemove', onResize);
    window.addEventListener('mouseup', endResize);
    e.preventDefault();
  }

  onUnmounted(() => {
    window.removeEventListener('mousemove', onResize);
    window.removeEventListener('mouseup', endResize);
  });

  return { width, resizing, startResize };
}
